/**
 * R-16 T-135 — the quick-links row, the pure half: which saved ids show (only those in the menus the reader can see),
 * the row's first ten and the rest under More, starring and the editor's moves. No React, no fetch.
 */
import type { MenuDecl, MenuItemDecl } from '@/features/apps/apps-api';

export const ROW_MAX = 10;

/** The saved ids as menu items, in order, dropping any id that is not in a menu the reader sees (hidden by role, permission or tier). */
export function quickLinkItems(ids: readonly string[], menus: readonly MenuDecl[]): MenuItemDecl[] {
  const byId = new Map(menus.flatMap((m) => m.items).map((it) => [it.id, it] as const));
  return [...new Set(ids)].flatMap((id) => {
    const it = byId.get(id);
    return it ? [it] : [];
  });
}

export function splitRow<T>(items: readonly T[], max = ROW_MAX): { row: T[]; more: T[] } {
  return { row: items.slice(0, max), more: items.slice(max) };
}

/** Star: a new id goes to the end; a starred id is removed. */
export function toggleId(ids: readonly string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
}

/** Move the item at `from` to `to` (the editor's drag and its up/down buttons). */
export function moveId(ids: readonly string[], from: number, to: number): string[] {
  if (from === to || from < 0 || from >= ids.length || to < 0 || to >= ids.length) return [...ids];
  const next = [...ids];
  const [it] = next.splice(from, 1);
  next.splice(to, 0, it!);
  return next;
}

/**
 * PR #88: what the editor saves — the reader's edited order of the links they can see, then every stored page their
 * tier hides right now (kept in order), so switching to Expert brings those back.
 */
export function keepHidden(edited: readonly string[], stored: readonly string[], visible: ReadonlySet<string>): string[] {
  return [...edited, ...stored.filter((id) => !visible.has(id) && !edited.includes(id))];
}

/** What the person asked for, applied when its save goes out (PR #88 round 4): add or remove one page, set a list, reset. */
type Intent = { add: string } | { remove: string } | { set: readonly string[] } | { reset: true };

const applyIntent = (list: readonly string[], i: Intent): string[] =>
  "add" in i ? (list.includes(i.add) ? [...list] : [...list, i.add]) : "remove" in i ? list.filter((x) => x !== i.remove) : "set" in i ? [...i.set] : [...list];

/**
 * PR #88 rounds 2–4: the reader's quick links as kept (`stored`, tier-hidden pages included), changed one save at a time.
 * Each star or save is an intent queued behind the one before; its list is built when it goes out, from the server's
 * last answer (`confirmed`), so two quick stars both survive and a refused save takes nothing with it — the intents
 * still waiting are re-applied to the rolled-back list. What the person sees is the server's list with every waiting
 * intent applied.
 */
export class SaveQueue {
  stored: string[];
  private confirmed: string[];
  private intents: Intent[] = [];
  private tail: Promise<unknown> = Promise.resolve();

  constructor(
    stored: readonly string[],
    private readonly send: (ids: string[] | null) => Promise<string[]>,
    private readonly onChange: () => void = () => {},
  ) {
    this.stored = [...stored];
    this.confirmed = [...stored];
  }

  private recompute() {
    this.stored = this.intents.reduce<string[]>(applyIntent, [...this.confirmed]);
    this.onChange();
  }

  has(id: string): boolean {
    return this.stored.includes(id);
  }

  /** the server's list (a fresh read); the intents still waiting are applied on top */
  sync(stored: readonly string[]): void {
    this.confirmed = [...stored];
    this.recompute();
  }

  toggle(id: string): Promise<string[]> {
    return this.enqueue(this.has(id) ? { remove: id } : { add: id });
  }

  /** `null` = back to the defaults (the server's answer then gives the list) */
  save(ids: string[] | null): Promise<string[]> {
    return this.enqueue(ids ? { set: ids } : { reset: true });
  }

  private enqueue(intent: Intent): Promise<string[]> {
    this.intents.push(intent);
    this.recompute();
    const done = () => {
      const at = this.intents.indexOf(intent);
      if (at >= 0) this.intents.splice(at, 1);
    };
    const run = () =>
      this.send("reset" in intent ? null : applyIntent(this.confirmed, intent)).then(
        (answer) => {
          this.confirmed = [...answer];
          done();
          this.recompute();
          return answer;
        },
        (e: unknown) => {
          // refused: this intent is dropped; what the person sees is the server's list with the waiting intents
          done();
          this.recompute();
          throw e;
        },
      );
    const p = this.tail.then(run, run);
    this.tail = p.catch(() => undefined);
    return p;
  }
}

/**
 * PR #88 round 3: what Save in the editor sends. After Reset to defaults the edited defaults replace the list (the old
 * tier-hidden pages are gone with it); otherwise the tier-hidden pages are kept after the visible ones (keepHidden).
 */
export function editorList(edited: readonly string[], reset: boolean, stored: readonly string[], visible: ReadonlySet<string>): string[] {
  return reset ? [...edited] : keepHidden(edited, stored, visible);
}

/**
 * PR #88 round 4: how many links fit in `available` px — every one if they all fit, else as many as leave room for
 * the More button (`more` px) after them. `gap` is the space between links.
 */
export function fitCount(widths: readonly number[], available: number, gap: number, more: number): number {
  const total = (k: number) => widths.slice(0, k).reduce((a, w) => a + w, 0) + gap * Math.max(0, k - 1);
  if (total(widths.length) <= available) return widths.length;
  let k = widths.length - 1;
  while (k > 0 && total(k) + gap + more > available) k--;
  return k;
}
